/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Configuration, QueryRequest, ROUTING} from '@adobe/exc-app/network';
import fetchApi from './fetch';
import {Level} from '@adobe/exc-app/metrics/Level';
import metrics from '../metrics';

const XQL = 'xql';

const NA = 'unknown';

let logger = metrics.create('exc.module-runtime.network.graphql');
let ioRegionSpecificMap: Record<string, string> = {};
let graphQLConfig = {
  apiGatewayUrl: '',
  appId: '',
  gqlEndpoint: '',
  ioGatewayUrl: '',
  ioRegionSpecificMap,
  xql: ''
};

const configure = (config: Configuration): void => {
  graphQLConfig = {
    apiGatewayUrl: config.apiGatewayUrl,
    appId: config.appId,
    gqlEndpoint: config.gqlEndpoint || '',
    ioGatewayUrl: config.ioGatewayUrl || '',
    ioRegionSpecificMap: config.ioRegionSpecificMap || {},
    xql: config.xqlGatewayUrl || ''
  };
};

const request = (input: QueryRequest): Promise<Response> => {
  const operationName = input.operationName;
  const graphQLQueries = operationName ? Object.assign({operationName}, input.data) : input.data;
  const appId = input.appId || graphQLConfig.appId;
  let auth: 'Body' | 'Header' = 'Body';
  let excludeSandboxType: boolean|undefined;
  let ioEndpoint: string|undefined = `${graphQLConfig.ioGatewayUrl}/graph/graphql?appId=${appId}`;
  let url = `${graphQLConfig.apiGatewayUrl}/api/gql/app/${appId}/graphql?appId=${appId}`;
  if (input.routing === ROUTING.AEP_PROFILE_BASED) {
    url = `${graphQLConfig.apiGatewayUrl}/api/gql/profile/graphql?appId=${appId}`;
  }
  if (graphQLConfig.gqlEndpoint) {
    url = graphQLConfig.gqlEndpoint;
  }
  // TODO: This logic needs to be removed once XQL has moved to federation (EXC-16583)
  if (appId === XQL && graphQLConfig.xql) {
    url = graphQLConfig.xql;
    auth = 'Header';
    excludeSandboxType = true;
    ioEndpoint = undefined;
  }

  logger.info(`Gql operation: ${operationName}, AppId: ${appId}`);
  let ioRegionSpecificMap;
  if (input.regionEnabled) {
    ioRegionSpecificMap = graphQLConfig.ioRegionSpecificMap;
  }
  return fetchApi.fetch(url, {
    auth,
    body: JSON.stringify(graphQLQueries),
    excludeSandbox: input.excludeSandbox,
    excludeSandboxType,
    headers: {
      'Content-Type': 'application/json'
    },
    metadata: input.metadata,
    method: 'POST'
  },
  ioEndpoint,
  ioRegionSpecificMap
  );
};

function logGqlError(error: string, operation: string = NA, requestId: string = NA): void {
  logger.event('GraphQL.Error', error, {
    appId: graphQLConfig.appId,
    error,
    operation,
    requestId
  }, {level: Level.ERROR});
}

function extractError(res: Response, operation: string = NA): void {
  const requestId: undefined | string = res.headers && res.headers.get('x-request-id') || NA;
  // Clone the response object so that a consumer of the API can read the stream again.
  res.clone().text()
    .then(error => logGqlError(error || 'Unknown error', operation, requestId));
}

function getOperationName(query: string): string|undefined {
  // Extract operation name from query
  const name = /(query|mutation) ?(\w+)/.exec(query);
  return (name && name.length) ? name[2] : undefined;
}

function query(input: QueryRequest): Promise<Response> {
  if (!input.operationName) {
    input.operationName = (!(input.data instanceof Array)) ? getOperationName(input.data.query) : undefined;
  }
  const operation: string = input.operationName && input.operationName.toString() || NA;
  return request(input)
    .then(res => {
      !res.ok && extractError(res, operation);
      return res;
    })
    .catch(error => {
      logGqlError(error || 'Unknown caught error', operation);
      throw error;
    });
}

export default {
  configure,
  query
};
